import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterTermsModal from './Company/RecruiterTermsModal';
import PostJobChoiceModal from './Company/PostJobChoiceModal';
import './ProfileSidebar.css';

const ProfileSidebar = ({ 
  isOpen, 
  onClose, 
  onProfileClick, 
  onAccountSettingsClick,
  onProfileStatusClick
}) => {
  const { user, logout, isAuthenticated, switchMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPostJobChoiceModal, setShowPostJobChoiceModal] = useState(false);
  const [pendingSwitchToCompany, setPendingSwitchToCompany] = useState(false);
  const [pendingCompanyUserId, setPendingCompanyUserId] = useState(null);

  const currentMode = user?.active_mode || user?.default_mode || 'personal';
  const isCompanyMode = currentMode === 'company';
  const isRecruiterMode = currentMode === 'recruiter';
  const isRecruiterOrCompanyMode = isCompanyMode || isRecruiterMode;
  const termsAccepted = user?.job_posting_terms_accepted || false;
  const displayName = (isCompanyMode && (user?.company_profile?.name || user?.active_company?.name))
    ? (user.company_profile?.name || user.active_company?.name)
    : (user?.name || 'User');
  
  // Extract first name for "Switch to [Firstname]" text. In company mode show parent's first name so user knows where they switch back to.
  const getUserFirstName = () => {
    if (isCompanyMode && user?.parent_user_id && user?.parent_name) {
      const nameParts = String(user.parent_name).trim().split(/\s+/);
      return nameParts[0] || 'Personal';
    }
    const name = user?.name || '';
    const nameParts = name.trim().split(/\s+/);
    return nameParts[0] || 'Personal';
  };

  const handlePostJobsClick = () => {
    // In recruiter or company mode: switch to personal mode
    if (isRecruiterOrCompanyMode) {
      handleModeSwitch();
      return;
    }
    // In personal mode: show choice modal (create company vs post directly)
    onClose();
    setShowPostJobChoiceModal(true);
  };

  const handleModeSwitch = async () => {
    if (isSwitchingMode) return;
    await switchModeDirectly('personal', false, true);
  };

  const switchModeDirectly = async (mode, acceptTerms = false, setAsDefault = false, companyUserId = null) => {
    setIsSwitchingMode(true);
    try {
      const result = await switchMode(mode, acceptTerms, setAsDefault, companyUserId);
      if (!result.success) {
        alert(result.message || 'Failed to switch mode');
      } else {
        setShowPostJobChoiceModal(false);
        if (location.pathname.startsWith('/profile')) {
          window.location.reload();
        } else {
          navigate('/profile');
        }
      }
    } catch (error) {
      alert(error.message || 'Failed to switch mode');
    } finally {
      setIsSwitchingMode(false);
    }
  };

  const handlePostJobDirect = async () => {
    setShowPostJobChoiceModal(false);
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }
    await switchModeDirectly('recruiter', false, true);
  };

  const handleTermsAccept = async () => {
    setShowTermsModal(false);
    const switchToCompany = pendingSwitchToCompany;
    const companyId = pendingCompanyUserId;
    setPendingSwitchToCompany(false);
    setPendingCompanyUserId(null);
    await switchModeDirectly(switchToCompany ? 'company' : 'recruiter', true, true, switchToCompany ? companyId : null);
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && event.target.closest('.profile-sidebar-overlay')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Touch gesture handlers for swipe to close
  const handleTouchStart = (e) => {
    if (!isOpen) return;
    const touch = e.touches[0];
    setTouchStart(touch.clientX);
    setTouchCurrent(touch.clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isOpen || !isDragging || touchStart === null) return;
    const touch = e.touches[0];
    setTouchCurrent(touch.clientX);
    
    // Only allow swiping left (closing)
    const diff = touchStart - touch.clientX;
    if (diff > 0 && sidebarRef.current) {
      const sidebarWidth = sidebarRef.current.offsetWidth;
      const translateX = Math.min(diff, sidebarWidth);
      sidebarRef.current.style.transform = `translateX(${translateX}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isOpen || !isDragging || touchStart === null || touchCurrent === null) {
      setIsDragging(false);
      setTouchStart(null);
      setTouchCurrent(null);
      if (sidebarRef.current) {
        sidebarRef.current.style.transform = '';
      }
      return;
    }

    const diff = touchStart - touchCurrent;
    const threshold = 100; // Minimum swipe distance to close (px)
    const sidebarWidth = sidebarRef.current?.offsetWidth || 387;
    const closeThreshold = sidebarWidth * 0.3; // Close if swiped 30% of width

    if (diff > threshold || diff > closeThreshold) {
      // Close the sidebar
      onClose();
    }

    // Reset
    setIsDragging(false);
    setTouchStart(null);
    setTouchCurrent(null);
    if (sidebarRef.current) {
      sidebarRef.current.style.transform = '';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="profile-sidebar-overlay"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`profile-sidebar ${isOpen ? 'profile-sidebar-open' : ''} ${isDragging ? 'profile-sidebar-dragging' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="profile-sidebar-header">
          <div className="profile-sidebar-user">
            <div className="profile-sidebar-avatar">
              {isCompanyMode && user?.active_company?.logo_url ? (
                <img src={user.active_company.logo_url} alt={displayName} />
              ) : user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={displayName} />
              ) : (
                <div className="profile-sidebar-default-avatar">
                  <span className="profile-sidebar-avatar-initial">
                    {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
              )}
            </div>
            <div className="profile-sidebar-user-info">
              <h3 className="profile-sidebar-user-name">
                {displayName}
              </h3>
              {user?.email && (
                <p className="profile-sidebar-user-email">
                  {user.email}
                </p>
              )}
            </div>
          </div>
          <button 
            className="profile-sidebar-close"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <nav className="profile-sidebar-nav">
          <button 
            className="profile-sidebar-item"
            onClick={() => {
              onProfileClick();
              onClose();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span>Dashboard</span>
          </button>

          {onProfileStatusClick && (
            <button 
              className="profile-sidebar-item"
              onClick={() => {
                onProfileStatusClick();
                onClose();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
              <span>Profile Status</span>
            </button>
          )}

          <div className="profile-sidebar-divider"></div>

          <button 
            className={`profile-sidebar-item ${!isRecruiterOrCompanyMode ? 'post-jobs-item' : ''}`}
            onClick={handlePostJobsClick}
            disabled={isSwitchingMode}
          >
            {isSwitchingMode ? (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>Switching...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isRecruiterOrCompanyMode ? (
                    <>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </>
                  ) : (
                    <>
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
                    </>
                  )}
                </svg>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isRecruiterOrCompanyMode
                    ? (isRecruiterMode ? 'Find Job' : getUserFirstName())
                    : 'Post Jobs'}
                  {isRecruiterOrCompanyMode && (
                    <span className="blinking-green-dot"></span>
                  )}
                </span>
              </>
            )}
          </button>

          {onAccountSettingsClick && (
            <button 
              className="profile-sidebar-item"
              onClick={() => {
                onAccountSettingsClick();
                onClose();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              <span>Settings</span>
            </button>
          )}

          <div className="profile-sidebar-divider"></div>

          <button 
            className="profile-sidebar-item profile-sidebar-item-logout"
            onClick={handleLogout}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16,17 21,12 16,7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Recruiter Terms Modal */}
      <RecruiterTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
      />

      {/* Post Job choice: create company vs post directly */}
      <PostJobChoiceModal
        isOpen={showPostJobChoiceModal}
        onClose={() => setShowPostJobChoiceModal(false)}
        onPostJobDirect={handlePostJobDirect}
        onCreateCompany={() => {
          setShowPostJobChoiceModal(false);
          onClose();
          navigate('/company/setup');
        }}
        onSelectCompany={(company) => {
          if (!termsAccepted) {
            setShowPostJobChoiceModal(false);
            setPendingCompanyUserId(company?.id ?? null);
            setPendingSwitchToCompany(true);
            setShowTermsModal(true);
            return;
          }
          switchModeDirectly('company', false, true, company?.id ?? null);
        }}
      />
    </>
  );
};

export default ProfileSidebar;
